/**
 * Parses scoring-results.md into ResultsGuide (tiers + "What's next?" content)
 * and checklist content into sections/items.
 */
import { ResultsGuide, ResultsTier } from '../types';
import type { ChecklistSection, ChecklistItem } from '../types';

/** Maximum input length to prevent ReDoS attacks */
const MAX_MARKDOWN_LENGTH = 500000; // 500KB limit

/** Precompiled regex patterns for parsing */
const SCORE_RANGE = /\*\*Score range:\*\* (.+?)(?:\n|$)/;
const HEADLINE = /### Headline\s*\n(.+?)(?=\n### |\n---|\n## |$)/s;
const SUMMARY = /### Summary\s*\n(.+?)(?=\n### |\n---|\n## |$)/s;
const WHAT_THIS_MEANS = /### What this means\s*\n(.+?)(?=\n### |\n---|\n## |$)/s;

/** Default empty results guide for invalid input */
const EMPTY_RESULTS_GUIDE: ResultsGuide = {
  title: 'AI-readiness checklist: Results',
  tiers: [],
  sectionScores: '',
  needHelp: '',
};

/**
 * Safely trims a string, returning empty string for invalid input
 */
function safeTrim(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

/** Parse a single ## Tier N: ... block from scoring-results.md into ResultsTier. */
function parseTierBlock(block: string): ResultsTier | null {
  if (typeof block !== 'string') return null;

  const trimmed = safeTrim(block);
  if (!trimmed) return null;

  const tierMatch = trimmed.match(/^## Tier (\d+): ([^\n]+?) ([^\s]+)\s*$/m);
  if (!tierMatch) return null;

  const level = parseInt(tierMatch[1], 10);
  // Validate tier level is within expected range
  if (!Number.isInteger(level) || level < 1 || level > 4) return null;

  const name = safeTrim(tierMatch[2]);
  const emoji = safeTrim(tierMatch[3]);

  const lines = trimmed.split('\n');
  const rest = lines.slice(1).join('\n');
  const scoreRangeMatch = rest.match(SCORE_RANGE);
  const scoreRange = scoreRangeMatch ? safeTrim(scoreRangeMatch[1]) : '';

  const headlineMatch = rest.match(HEADLINE);
  const headline = headlineMatch ? safeTrim(headlineMatch[1]) : '';

  const summaryMatch = rest.match(SUMMARY);
  const summary = summaryMatch ? safeTrim(summaryMatch[1]) : '';

  const whatMatch = rest.match(WHAT_THIS_MEANS);
  const whatThisMeans = whatMatch ? safeTrim(whatMatch[1]) : '';

  // Body: everything after "### What this means" section until end of block
  const afterWhat = whatMatch
    ? rest.slice(rest.indexOf(whatMatch[0]) + whatMatch[0].length).trim()
    : rest;
  const body = afterWhat.replace(/^\n+/, '').trim();

  return {
    id: `tier-${level}`,
    level,
    name,
    emoji,
    scoreRange,
    headline,
    summary,
    whatThisMeans,
    body,
  };
}

function extractTitle(markdown: string): string {
  if (typeof markdown !== 'string') return 'AI-readiness checklist: Results';
  const match = markdown.match(/^# (.+?)(?:\n|$)/m);
  return match ? safeTrim(match[1]) : 'AI-readiness checklist: Results';
}

/**
 * Parse the results guide markdown (new checklist.md structure).
 * Structure: # Title, ---, ## Tier 4..., ---, ## Tier 3..., ---, ## Tier 2..., ---, ## Tier 1..., ---, ## Understanding your section scores..., ## Need help?...
 */
export function parseResultsGuide(markdown: string): ResultsGuide {
  // Validate input
  if (typeof markdown !== 'string') {
    return EMPTY_RESULTS_GUIDE;
  }

  // Prevent ReDoS by limiting input size
  if (markdown.length > MAX_MARKDOWN_LENGTH) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Markdown input exceeds maximum length');
    }
    return EMPTY_RESULTS_GUIDE;
  }

  const title = extractTitle(markdown);

  const parts = markdown.split(/\n---\n/).map((p) => safeTrim(p));

  // parts[0] = "# Title\n\n" or similar
  // parts[1] = "## Tier 4: ..." , parts[2] = "## Tier 3: ..." , etc.
  // After tiers we have "## Understanding your section scores" and "## Need help?"
  const tiers: ResultsTier[] = [];
  let sectionScores = '';
  let needHelp = '';

  for (let i = 1; i < parts.length; i++) {
    const block = parts[i];
    if (!block) continue;

    if (block.startsWith('## Understanding your section scores')) {
      const needHelpStart = block.indexOf('\n## Need help?');
      if (needHelpStart !== -1) {
        sectionScores = block
          .slice(0, needHelpStart)
          .replace(/^## Understanding your section scores\s*\n?/, '')
          .trim();
        needHelp = block
          .slice(needHelpStart)
          .replace(/^\n## Need help\?\s*\n?/, '')
          .trim();
      } else {
        sectionScores = block.replace(/^## Understanding your section scores\s*\n?/, '').trim();
      }
      if (needHelpStart !== -1) break;
      continue;
    }

    if (block.startsWith('## Need help?')) {
      needHelp = block.replace(/^## Need help\?\s*\n?/, '').trim();
      break;
    }

    const tier = parseTierBlock(block);
    if (tier) {
      tiers.push(tier);
    }
  }

  // If we didn't find section scores / need help via --- split, scan for them
  if (!sectionScores && !needHelp) {
    const sectionScoresMatch = markdown.match(/## Understanding your section scores\s*\n([\s\S]+?)(?=\n## Need help\?|$)/);
    if (sectionScoresMatch) {
      sectionScores = sectionScoresMatch[1].trim();
    }
    const needHelpMatch = markdown.match(/## Need help\?\s*\n([\s\S]+)/);
    if (needHelpMatch) {
      needHelp = needHelpMatch[1].trim();
    }
  }

  return {
    title,
    tiers,
    sectionScores,
    needHelp,
  };
}

export interface ChecklistContent {
  hero: string;
  intro: string;
  sections: ChecklistSection[];
}

/** Default empty checklist content for invalid input */
const EMPTY_CHECKLIST_CONTENT: ChecklistContent = {
  hero: '',
  intro: '',
  sections: [],
};

/**
 * Parse quiz/checklist content (content.md).
 * hero = opening block until first --- (replaces Hero copy).
 * intro = from first --- until ## Section 1: (## How AI... etc.).
 */
export function parseChecklistContent(markdown: string): ChecklistContent {
  // Validate input
  if (typeof markdown !== 'string') {
    return EMPTY_CHECKLIST_CONTENT;
  }

  // Prevent ReDoS by limiting input size
  if (markdown.length > MAX_MARKDOWN_LENGTH) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Markdown input exceeds maximum length');
    }
    return EMPTY_CHECKLIST_CONTENT;
  }

  const lines = markdown.split('\n');
  const sections: ChecklistSection[] = [];

  const section1Match = markdown.match(/\n## Section 1:/);
  let hero = '';
  let intro = '';
  if (section1Match && section1Match.index !== undefined) {
    const beforeSections = markdown.slice(0, section1Match.index).trim();
    const firstHr = beforeSections.indexOf('\n---\n');
    if (firstHr !== -1) {
      hero = beforeSections.slice(0, firstHr).trim();
      intro = beforeSections.slice(firstHr + 5).trim();
    } else {
      intro = beforeSections;
    }
  }
  let currentSection: ChecklistSection | null = null;
  let currentItem: Partial<ChecklistItem> | null = null;
  let contentLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.match(/^## Section \d+:/)) {
      if (currentSection && currentItem) {
        currentItem.content = contentLines.join('\n').trim();
        currentSection.items.push(currentItem as ChecklistItem);
        currentItem = null;
        contentLines = [];
      }

      const sectionMatch = line.match(/^## Section (\d+): (.+)/);
      if (sectionMatch) {
        let time = '';
        const nextLine = lines[i + 1];
        if (nextLine?.match(/\*.*[Aa]verage implementation time/)) {
          const timeMatch = nextLine.match(/\*([^*]+)\*/);
          time = timeMatch ? timeMatch[1].trim() : nextLine.replace(/^\s*\*\s*|\s*\*\s*$/g, '').trim();
          i += 1;
        }

        currentSection = {
          id: `section-${sectionMatch[1]}`,
          number: parseInt(sectionMatch[1], 10),
          title: sectionMatch[2].trim(),
          weight: '',
          timeToImplement: time,
          items: [],
        };
        sections.push(currentSection);
      }
      continue;
    }

    if (line.match(/^### ☐ \d+\./)) {
      if (currentItem && currentSection) {
        currentItem.content = contentLines.join('\n').trim();
        currentSection.items.push(currentItem as ChecklistItem);
        contentLines = [];
      }

      const itemMatch = line.match(/^### ☐ (\d+)\. (.+)/);
      if (itemMatch && currentSection) {
        const itemNumber = parseInt(itemMatch[1], 10);
        let difficulty: 'quick' | 'medium' | 'significant' = 'medium';
        let impact = '';
        const nextLine = lines[i + 1];
        if (nextLine) {
          if (nextLine.includes('🟢 Quick win')) difficulty = 'quick';
          else if (nextLine.includes('🟡 Medium effort')) difficulty = 'medium';
          else if (nextLine.includes('🔴 Significant investment')) difficulty = 'significant';
          const pipeIdx = nextLine.indexOf('|');
          impact = pipeIdx >= 0 ? nextLine.slice(pipeIdx + 1).trim() : '';
        }

        currentItem = {
          id: `item-${itemNumber}`,
          number: itemNumber,
          title: itemMatch[2].trim(),
          difficulty,
          impact,
          section: currentSection.id,
          content: '',
        };
      }
      continue;
    }

    if (currentItem && !line.match(/^## Section/) && !line.match(/^### ☐/)) {
      if (!line.match(/^(🟢|🟡|🔴)/)) {
        contentLines.push(line);
      }
    }
  }

  if (currentItem && currentSection) {
    currentItem.content = contentLines.join('\n').trim();
    currentSection.items.push(currentItem as ChecklistItem);
  }

  return { hero, intro, sections };
}
